import { useState, type FormEvent } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    formData.delete('company_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9rf61fnahb7382ovli0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const faqData = [
    { q: 'jm漫画的漫画都是正版的吗？', a: '是的，jm漫画平台上的所有漫画作品均为正版授权内容。我们与国内外多家知名出版社和原创作者直接合作，确保每一部作品都有合法授权，让读者可以安心阅读，同时也支持原创漫画作者的创作。' },
    { q: 'jm漫画支持哪些设备阅读？', a: 'jm漫画支持手机（iOS和Android）、平板电脑和PC网页端等多种设备。您可以在不同设备上无缝切换阅读，阅读进度会自动同步。我们还提供离线下载功能，让您在没有网络的情况下也能畅快阅读。' },
    { q: 'jm漫画的更新频率是怎样的？', a: 'jm漫画坚持每日更新，热门连载作品与日本同步更新最新章节。我们的更新团队7×24小时待命，确保读者能够在第一时间阅读到最新内容。永不断更是我们对读者的承诺。' },
    { q: '如何在jm漫画上找到喜欢的漫画？', a: '您可以通过首页的热门推荐、分类浏览或搜索功能找到喜欢的漫画。jm漫画的智能推荐系统会根据您的阅读历史，精准推荐您可能感兴趣的漫画作品，越读越懂你的喜好。' },
    { q: 'jm漫画是否需要付费？', a: 'jm漫画提供大量免费漫画内容供读者阅读。注册即可免费阅读精选内容，部分独家作品和超前章节可能需要会员订阅。我们提供多种会员方案，性价比极高，满足不同读者的需求。' },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - jm漫画 | 品牌介绍与联系方式"
        description="了解jm漫画的品牌故事、平台优势与核心特色。jm漫画是深受用户喜爱的正版在线漫画阅读平台，拥有海量漫画资源，涵盖热血、恋爱、悬疑、奇幻等多种类型。"
        keywords="jm漫画,关于jm漫画,jm漫画品牌介绍,漫画平台,联系我们"
        canonicalUrl="https://jmcomic.com/about"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <Navbar />

      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20illustration%20of%20an%20immersive%20reading%20environment%20with%20floating%20comic%20book%20pages%20and%20panels%20in%20warm%20golden%20and%20crimson%20tones%2C%20soft%20atmospheric%20lighting%2C%20cozy%20library%20aesthetic%20blended%20with%20modern%20digital%20elements%2C%20no%20text%2C%20cinematic%20composition&width=1400&height=500&seq=about-hero-comic-01&orientation=landscape"
                alt="jm漫画品牌故事"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
            </div>
            <div className="relative z-10 py-16 md:py-24 px-6 md:px-12">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-4">
                关于 jm漫画
              </h1>
              <p className="text-sm md:text-base text-foreground-100/80 max-w-xl leading-relaxed">
                jm漫画创立于2018年，致力于为全球华人漫画爱好者打造一个高品质的正版漫画阅读平台。
                我们相信，每一部好的漫画作品都值得被更多人看见，每一位创作者的努力都值得被尊重。
              </p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">品牌故事</h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  jm漫画的创始团队由一群热爱漫画的年轻人组成。在创立jm漫画之前，团队成员分散在不同的漫画论坛和阅读平台上，深切感受到国内漫画爱好者面临的痛点——资源分散、更新滞后、画质参差不齐、盗版横行。
                </p>
                <p>
                  带着"让每个人都能轻松享受正版漫画"的初心，我们于2018年正式推出了jm漫画平台。从一开始的几十部漫画起步，到如今拥有<strong>超过10,000部正版漫画</strong>资源，jm漫画已经成长为国内领先的正版漫画阅读平台之一。
                </p>
                <p>
                  我们与日本集英社、讲谈社、小学馆等国际知名出版社，以及国内众多原创漫画工作室建立了深度合作关系。每一部上架的作品都经过严格的版权审核，确保读者看到的都是正版授权内容。
                </p>
                <p>
                  六年来，jm漫画累计服务<strong>超过5,000万用户</strong>，日均活跃读者突破<strong>800万</strong>。这些数字的背后，是每一位读者对我们的信任与支持，也是我们不断前进的动力。
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inspiring%20illustration%20of%20a%20creative%20team%20workspace%20with%20comic%20art%20displayed%20on%20screens%20and%20walls%2C%20manga%20style%20decorations%2C%20cozy%20modern%20office%20with%20warm%20lighting%2C%20creative%20atmosphere%2C%20soft%20focus%2C%20artistic%20vibe&width=800&height=600&seq=about-story-comic-02&orientation=landscape"
                alt="jm漫画创作团队"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">发展历程</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { year: '2018', title: '平台创立', desc: 'jm漫画正式上线，首批上架200部正版漫画' },
              { year: '2020', title: '用户突破', desc: '注册用户突破1000万，日活用户超200万' },
              { year: '2023', title: '全面升级', desc: '推出AI智能推荐系统，新增离线下载功能' },
              { year: '2026', title: '行业领先', desc: '累计服务超5000万用户，正版漫画超10,000部' },
            ].map((item) => (
              <div key={item.year} className="text-center p-5 rounded-xl bg-background-100 border border-background-200/60">
                <p className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">{item.year}</p>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-xs text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="max-w-7xl mx-auto px-4 md:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">用户口碑</h2>
          <p className="text-sm text-foreground-500 text-center mb-10">来自真实读者的评价与反馈</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 font-semibold text-xs">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">常见问题</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqData.map((item, index) => (
              <details key={index} className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-semibold text-foreground-900 pr-4">{item.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="max-w-7xl mx-auto px-4 md:px-8 mt-20">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-4">联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mb-10">有任何问题或建议？我们很乐意听取你的意见</p>

          <div className="max-w-xl mx-auto">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的姓名"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="请输入您的邮箱地址"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                >
                  <option value="">请选择反馈类型</option>
                  <option value="版权问题">版权问题</option>
                  <option value="内容建议">内容建议</option>
                  <option value="技术问题">技术问题</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="请输入您想说的内容（最多500字）"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>

              <div className="honeypot-field">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {submitting ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <div className="text-center py-3 px-4 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  <i className="ri-check-line mr-1.5"></i>
                  感谢您的留言！我们会尽快回复您。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="text-center py-3 px-4 rounded-lg bg-red-50 text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}
            </form>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">客服邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">support@jmcomic.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">客服电话</p>
                  <p className="text-sm font-medium text-foreground-800">400-888-6666</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                  <i className="ri-time-line text-secondary-600"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">工作时间</p>
                  <p className="text-sm font-medium text-foreground-800">周一至周日 9:00-21:00</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}